import React, { useState, useRef, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useHistory } from 'react-router-dom';
import { useQuery, useMutation } from '@apollo/client';
import { makeStyles } from '@material-ui/core/styles';
import PropTypes from 'prop-types';
import Dialog from '@material-ui/core/Dialog';
import DialogContent from '@material-ui/core/DialogContent';
import DialogTitle from '@material-ui/core/DialogTitle';
import _ from 'lodash';

import {
    Text,
    CrossIcon,
    IconButton,
    NotificationCard,
    NoInformation,
    ConfirmDialog,
    DeleteIcon,
    Progress,
} from '../../index';

import {
    GET_NOTIFICATION,
    UPDATE_NOTIFICATION_ISREAD,
    deleteNotification,
    deleteAllNotification,
} from '../../../services/index';

import { alertError, alertSuccess } from '../../../redux/stateActions';

import socket from '../../../socket';

const Notification = ({ open, handleClose }) => {
    const history = useHistory();
    const classes = useStyles();
    const user = useSelector((state) => state.user);
    const dispatch = useDispatch();

    const descriptionElementRef = useRef(null);
    React.useEffect(() => {
        if (open) {
            const { current: descriptionElement } = descriptionElementRef;
            if (descriptionElement !== null) {
                descriptionElement.focus();
            }
        }
    }, [open]);

    /*----- Get Notifications -----*/
    const [noticeData, setNoticeData] = useState([]);
    const {
        data: getNotificationsData,
        error: getNotificationsError,
        refetch: getNotificationsRefetch,
    } = useQuery(GET_NOTIFICATION, {
        variables: { id: user.staff.id },
        skip: _.isEmpty(user),
    });

    useEffect(() => {
        if (getNotificationsError) {
            dispatch(alertError(`Cannot get notifications`));
        }
        if (getNotificationsData) {
            setNoticeData(getNotificationsData.notifications);
        }
    }, [getNotificationsError, getNotificationsData]);

    useEffect(() => {
        socket.on('newNotification', () => {
            getNotificationsRefetch();
        });
    }, []);

    //Handle update on Read notificstion
    const [
        updateNotificationRead,
        { data: updateNotificationData, error: updateNotificationError },
    ] = useMutation(UPDATE_NOTIFICATION_ISREAD);

    useEffect(() => {
        if (updateNotificationError) {
            dispatch(
                alertError(`Cannot upate notification to Read: ${updateNotificationError.message}`)
            );
        }
        if (updateNotificationData) {
            getNotificationsRefetch();
        }
    }, [updateNotificationData, updateNotificationError]);

    const updateUserRead = (noti) => {
        if (!noti.isRead) {
            updateNotificationRead({ variables: { id: noti.id } });
        }
    };

    /*----- Delete Notifications -----*/
    const [selectedNoti, setSelectdNoti] = useState(null);
    const [openConfirm, setOpenConfirm] = useState(false);

    const handleDelete = (id) => {
        setSelectdNoti(id);
        setOpenConfirm(true);
    };

    const handleCancelDelete = () => {
        setSelectdNoti(null);
        setOpenConfirm(false);
    };

    const handleDeleteNotification = async () => {
        const newNoticeData = JSON.parse(JSON.stringify(noticeData));
        const filterNotice = newNoticeData.filter((noti) => noti.id !== selectedNoti);
        setNoticeData(filterNotice);
        setOpenConfirm(false);

        try {
            const res = await deleteNotification(selectedNoti);

            if (res.status === 200) {
                getNotificationsRefetch();
            } else {
                throw new Error(res);
            }
        } catch (error) {
            dispatch(alertError(`${JSON.stringify(error.response?.data?.message)}`));
        }

        setSelectdNoti(null);
    };

    /*----- Delete All Notifications -----*/
    const [deleteLoading, setDeleteLoading] = useState(false);
    const [openConfirmAll, setOpenConfirmAll] = useState(false);

    const onClickDeleteAll = () => {
        setOpenConfirmAll(true);
    };

    const handleCancelDeleteAll = () => {
        setOpenConfirmAll(false);
    };

    const handleDeleteAllNotification = async () => {
        try {
            setOpenConfirmAll(false);

            setDeleteLoading(true);

            const res = await deleteAllNotification(user.staff.id);

            if (res.status === 200) {
                setDeleteLoading(false);

                dispatch(alertSuccess(`Delete all notification Success`));
                getNotificationsRefetch();
            } else {
                throw new Error(res);
            }
        } catch (error) {
            setDeleteLoading(false);

            dispatch(alertError(`${JSON.stringify(error.response?.data?.message)}`));
        }
    };

    const handleDetail = (data) => {
        switch (data.type) {
            case 'SESSION_ENDED':
                return history.push(
                    `/provider/session/${
                        data.session.meet.type === 'group' ? 'group-report' : 'report'
                    }/${data.session.meet.id}`
                );
            case 'WELCOME':
                return history.push(`/provider/profile/${user.staff.id}`);
        }
    };

    return (
        <>
            <ConfirmDialog
                id="confirm-delete-notification"
                description="modal-for-delete-notification"
                open={openConfirm}
                title="Delete ?"
                contentText="Once you delete messages. You can’t undo it. 
                Are you sure you want to continue ?"
                onCancel={handleCancelDelete}
                cancelText="Cancel"
                onConfirm={handleDeleteNotification}
                confirmText="Confirm"
                confirmBackgroundColor="pink"
            />

            <ConfirmDialog
                id="confirm-delete-all-notification"
                description="modal-for-delete-all-notification"
                open={openConfirmAll}
                title="Delete all ?"
                contentText="Once you delete all messages. You can’t undo it. 
                Are you sure you want to continue ?"
                onCancel={handleCancelDeleteAll}
                cancelText="Cancel"
                onConfirm={handleDeleteAllNotification}
                confirmText="Confirm"
                confirmBackgroundColor="pink"
            />
            <Dialog
                open={open}
                onClose={handleClose}
                scroll="paper"
                aria-labelledby="scroll-dialog-title"
                aria-describedby="scroll-dialog-description"
                fullScreen
                classes={{
                    container: classes.container,
                    paper: classes.paper,
                }}
            >
                {deleteLoading && <Progress />}
                <DialogTitle
                    id="scroll-dialog-title"
                    disableTypography
                    classes={{
                        root: classes.title,
                    }}
                >
                    <IconButton onClick={handleClose} style={{ justifySelf: 'end' }}>
                        <CrossIcon fill="gray1" />
                    </IconButton>
                    <Text $bold>Notification</Text>
                    <IconButton margin="0  0 0 10px" onClick={onClickDeleteAll}>
                        <DeleteIcon fill="black" />
                    </IconButton>
                </DialogTitle>
                <DialogContent classes={{ root: classes.content }}>
                    {!_.isEmpty(noticeData) ? (
                        noticeData.map((data) => (
                            <div key={`notification-${data.id}`} style={{ marginBottom: '5px' }}>
                                <NotificationCard
                                    role="staff"
                                    data={data}
                                    readNotification={updateUserRead}
                                    onDelete={handleDelete}
                                    onDetail={handleDetail}
                                />
                            </div>
                        ))
                    ) : (
                        <NoInformation />
                    )}
                </DialogContent>
            </Dialog>
        </>
    );
};

Notification.propTypes = {
    open: PropTypes.bool,
    handleClose: PropTypes.func,
};

export default Notification;

const useStyles = makeStyles(() => ({
    container: {
        justifyContent: 'flex-end',
        borderRadius: '16px',
    },
    paper: {
        width: '375px',
        borderRadius: '16px',
        paddingBottom: '20px',
        background:
            'radial-gradient(140.2% 109.33% at 89.67% 23.67%, rgba(208, 233, 255, 0.264) 9.88%, rgba(255, 226, 249, 0.356) 92.19%) , radial-gradient(190.06% 107.67% at 57.67% -7.67%, #C9FCFF 6.74%, rgba(246, 222, 255, 0.76) 100%)',
    },
    title: {
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
    },
    content: {
        display: 'flex',
        flexDirection: 'column',
        rowGap: '5px',
        paddingTop: 0,
    },
}));
